import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from 'helpers/theme';
import { fetchQuery, graphql } from 'relay-runtime';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCheck } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import Button from 'components/Button/Button';
import SearchableDropdown, {
  Course
} from 'sharedComponents/core/Input/SearchableDropdown';
import BookingDelegatePicker, {
  BookingDelegate
} from './BookingDelegatePicker';
import ClassroomInstanceSelector from './ClassroomInstanceSelector';
import CheckboxSingle from 'components/core/Input/CheckboxSingle';
import Payment from 'sharedComponents/Payment';
import BookingTerms from './BookingTerms';
import environment from 'api/environment';
import { GraphError } from 'types/general';
import { useModalRegistration } from 'views/ModalRegistryProvider';
import headerCheckerboard from 'assets/logo/header-checkerboard-booking.svg';
import { BookingModal_courses_QueryResponse } from './__generated__/BookingModal_courses_Query.graphql';

const useStyles = createUseStyles((theme: Theme) => ({
  '@keyframes overlayIn': {
    from: { opacity: 0 },
    to: { opacity: 1 }
  },
  '@keyframes panelIn': {
    from: { opacity: 0, transform: 'scale(0.94) translateY(24px)' },
    to: { opacity: 1, transform: 'scale(1) translateY(0)' }
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingTop: '7vh',
    background: 'rgba(23, 25, 35, 0.18)',
    animation: '$overlayIn 0.15s ease-out'
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    width: 640,
    maxWidth: 'calc(100vw - 40px)',
    maxHeight: '86vh',
    background: 'white',
    borderRadius: 14,
    border: '1px solid #ededed',
    boxShadow: '0 16px 70px rgba(0, 0, 0, 0.2), 0 3px 12px rgba(0, 0, 0, 0.08)',
    animation: '$panelIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
    overflow: 'hidden'
  },
  cover: {
    position: 'relative',
    flexShrink: 0,
    height: 84,
    background: `
      url(${headerCheckerboard}) left top / auto 100% no-repeat,
      linear-gradient(135deg, #f4f7ff, #eef3ff)
    `,
    borderBottom: '1px solid #ededed'
  },
  closeButton: {
    position: 'absolute',
    top: 14,
    right: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
    borderRadius: '50%',
    background: 'white',
    border: '1px solid #ededed',
    cursor: 'pointer',
    color: '#74777f',
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: '#f0f1f3'
    }
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gridGap: 16,
    flexWrap: 'wrap',
    padding: '18px 24px 14px'
  },
  title: {
    fontSize: 17,
    fontWeight: 700,
    color: theme.colors.primaryBlack
  },
  subtitle: {
    marginTop: 4,
    fontSize: 13,
    color: '#74777f'
  },
  steps: {
    display: 'flex',
    alignItems: 'center',
    gridGap: 18
  },
  step: {
    display: 'flex',
    alignItems: 'center',
    gridGap: 7,
    fontSize: 12,
    fontWeight: 500,
    color: '#9ea2ad'
  },
  stepActive: {
    extend: 'step',
    color: '#0b57ff'
  },
  stepDone: {
    extend: 'step',
    color: theme.colors.primaryBlack
  },
  stepDot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    borderRadius: '50%',
    fontSize: 11,
    background: '#f8f9fb',
    border: '1px solid #ededed',
    color: '#9ea2ad'
  },
  stepDotActive: {
    extend: 'stepDot',
    background: '#0b57ff',
    borderColor: '#0b57ff',
    color: 'white'
  },
  stepDotDone: {
    extend: 'stepDot',
    background: '#f0f1f3',
    color: theme.colors.primaryBlack
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '4px 24px 24px'
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#9ea2ad',
    margin: '18px 0 10px',
    '&:first-child': {
      marginTop: 0
    }
  },
  helpText: {
    fontSize: 13,
    color: '#74777f',
    lineHeight: 1.5,
    margin: '0 0 14px'
  },
  termsBox: {
    border: '1px solid #ededed',
    borderRadius: 10,
    overflow: 'hidden',
    marginBottom: 16
  },
  termsTitle: {
    padding: '10px 14px',
    fontSize: 13,
    fontWeight: 600,
    color: theme.colors.primaryBlack,
    background: '#f8f9fb',
    borderBottom: '1px solid #ededed'
  },
  termsContent: {
    maxHeight: 220,
    overflowY: 'auto',
    padding: '0 14px',
    fontSize: 13,
    color: '#4a4d55',
    lineHeight: 1.55
  },
  courseList: {
    border: '1px solid #ededed',
    borderRadius: 10,
    overflow: 'hidden',
    marginBottom: 14
  },
  courseListItem: {
    padding: '11px 14px',
    fontSize: 13,
    fontWeight: 500,
    color: theme.colors.primaryBlack,
    borderBottom: '1px solid #ededed',
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  checkboxRow: {
    padding: '4px 0 0'
  },
  successPanel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '40px 24px 28px'
  },
  successIcon: {
    fontSize: 46,
    color: '#18c329',
    marginBottom: 18
  },
  successTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: theme.colors.primaryBlack,
    marginBottom: 8
  },
  successText: {
    maxWidth: 400,
    fontSize: 13,
    color: '#74777f',
    lineHeight: 1.5,
    margin: 0
  },
  errorBanner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gridGap: 10,
    padding: '12px 14px',
    marginTop: 14,
    fontSize: 13,
    color: '#b3261e',
    background: '#fef1f2',
    border: '1px solid #fadcdf',
    borderRadius: 8
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gridGap: 10,
    padding: '16px 24px',
    borderTop: '1px solid #ededed'
  },
  footerButtons: {
    display: 'flex',
    gridGap: 10,
    marginLeft: 'auto'
  },
  total: {
    fontSize: 13,
    color: '#74777f'
  },
  totalValue: {
    marginLeft: 8,
    fontSize: 15,
    fontWeight: 700,
    color: theme.colors.primaryBlack
  }
}));

const courseSearchFunction = async (
  categoryUUID?: string,
  secondaryCategoryUUID?: string,
  text?: string
) => {
  const query = graphql`
    query BookingModal_courses_Query(
      $categoryUUID: UUID
      $secondaryCategoryUUID: UUID
      $name: String
    ) {
      courses(
        filter: {
          categoryUUID: $categoryUUID
          secondaryCategoryUUID: $secondaryCategoryUUID
          searchText: $name
        }
        page: { limit: 200 }
        orderBy: { ascending: false, field: "priority" }
      ) {
        edges {
          ident: id
          name
          price
          role
          category {
            uuid
          }
          secondaryCategory {
            uuid
          }
          backgroundCheck
          specificTerms
        }
      }
    }
  `;

  const data = (await fetchQuery(environment, query, {
    categoryUUID,
    secondaryCategoryUUID,
    name: text
  })) as BookingModal_courses_QueryResponse;

  if (!data || !data.courses || !data.courses.edges) {
    console.error('Could not get course data', data);
    return [];
  }

  const results: Course[] = [];
  data.courses.edges.forEach((course) => {
    results.push({
      id: course?.ident || 0,
      name: course?.name || '',
      price: course?.price || 0,
      role: course?.role || '',
      backgroundCheck: course?.backgroundCheck || false,
      specificTerms: course?.specificTerms || '',
      category: course?.category ?? undefined,
      secondaryCategory: course?.secondaryCategory ?? undefined
    });
  });
  return results;
};

type StepKey = 'courses' | 'terms' | 'payment';

const steps: { key: StepKey; label: string }[] = [
  { key: 'courses', label: 'Courses' },
  { key: 'terms', label: 'Terms' },
  { key: 'payment', label: 'Payment' }
];

const stepSubtitles: Record<StepKey, string> = {
  courses: 'Pick a course and the delegates to book onto it',
  terms: 'Review and accept our terms of business',
  payment: 'Confirm and pay for your booking'
};

type BookingState = {
  courses: any[];
  users: BookingDelegate[];
  termsAccepted?: boolean;
  backgroundCheckAccepted?: boolean;
  success?: boolean;
  error?: GraphError;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  isContract: boolean;
  categories: { edges: ReadonlyArray<any> | null } | null | undefined;
  initialCourse?: Course;
  initialUser?: { key: string; uuid: string; value: string };
};

const BookingModal = ({
  isOpen,
  onClose,
  isContract,
  categories,
  initialCourse,
  initialUser
}: Props) => {
  const theme = useTheme();
  const classes = useStyles({ theme });
  useModalRegistration(isOpen);

  const [step, setStep] = React.useState<StepKey>('courses');
  const [state, setState] = React.useState<BookingState>({
    courses: [],
    users: []
  });

  // Reset the flow whenever the modal opens
  React.useEffect(() => {
    if (!isOpen) return;
    setStep('courses');
    setState({
      courses: initialCourse ? [initialCourse] : [],
      users: initialUser ? [initialUser] : []
    });
  }, [isOpen, initialCourse, initialUser]);

  React.useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (evt: KeyboardEvent) => {
      if (evt.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const placeOrder = React.useRef<() => void>();

  const onInstanceSelected = React.useCallback((instance: any) => {
    setState((s) => ({
      ...s,
      courses: [{ ...s.courses[0], instanceUUID: instance.uuid }]
    }));
  }, []);

  if (!isOpen) return null;

  const delegateCount = state.users?.filter(Boolean).length ?? 0;
  const total =
    state.courses && state.courses.length > 0
      ? state.courses[0].price * delegateCount
      : 0;

  const backgroundCheckCourses =
    state.courses?.filter((course: Course) => !!course.backgroundCheck) ?? [];
  const requiresBackgroundChecks = backgroundCheckCourses.length > 0;

  const stepIndex = steps.findIndex((s) => s.key === step);

  const stepChip = (target: { key: StepKey; label: string }, index: number) => {
    const isActive = target.key === step;
    const isDone = index < stepIndex || (state.success ?? false);
    return (
      <div
        key={target.key}
        className={
          isActive && !state.success
            ? classes.stepActive
            : isDone
            ? classes.stepDone
            : classes.step
        }
      >
        <span
          className={
            isActive && !state.success
              ? classes.stepDotActive
              : isDone
              ? classes.stepDotDone
              : classes.stepDot
          }
        >
          {isDone ? <FontAwesomeIcon icon={faCheck} /> : index + 1}
        </span>
        {target.label}
      </div>
    );
  };

  const footerTotal =
    total > 0 ? (
      <div className={classes.total}>
        Total
        <span className={classes.totalValue}>£{total.toFixed(2)}</span>
      </div>
    ) : (
      <div />
    );

  return (
    <div className={classes.overlay} onMouseDown={onClose}>
      <div
        className={classes.panel}
        onMouseDown={(evt) => evt.stopPropagation()}
      >
        <div className={classes.cover}>
          <div className={classes.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </div>
        </div>
        <div className={classes.titleRow}>
          <div>
            <div className={classes.title}>
              Quick Booking
              {delegateCount > 0 && !state.success
                ? ` for ${delegateCount} Delegate${
                    delegateCount > 1 ? 's' : ''
                  }`
                : ''}
            </div>
            <div className={classes.subtitle}>
              {state.success ? 'Booking confirmed' : stepSubtitles[step]}
            </div>
          </div>
          <div className={classes.steps}>{steps.map(stepChip)}</div>
        </div>
        <div className={classes.body}>
          {step === 'courses' && (
            <>
              <div className={classes.sectionLabel}>Course</div>
              <SearchableDropdown
                selected={state.courses}
                categories={((categories?.edges ?? []) as unknown) as any[]}
                searchQuery={courseSearchFunction}
                setSelected={(selected) =>
                  setState((s) => ({ ...s, courses: selected }))
                }
              />
              {state.courses?.length > 0 && (
                <ClassroomInstanceSelector
                  courseID={state.courses[0].id || state.courses[0].ident}
                  onSelected={onInstanceSelected}
                />
              )}
              <div className={classes.sectionLabel}>Delegates</div>
              <BookingDelegatePicker
                selected={state.users}
                setSelected={(users) => setState((s) => ({ ...s, users }))}
              />
            </>
          )}
          {step === 'terms' && (
            <>
              <p className={classes.helpText}>
                To book your {delegateCount} delegate
                {delegateCount > 1 ? 's' : ''} onto this course you will need to
                agree to our terms of business below.
              </p>
              <div className={classes.termsBox}>
                <div className={classes.termsTitle}>
                  TTC Hub — Terms of Business
                </div>
                <div className={classes.termsContent}>
                  <BookingTerms />
                  {state.courses?.map(
                    (course: Course) =>
                      course.specificTerms && (
                        <p key={course.id}>{course.specificTerms}</p>
                      )
                  )}
                </div>
              </div>
              {requiresBackgroundChecks && (
                <>
                  <div className={classes.sectionLabel}>Background Check</div>
                  <p className={classes.helpText}>
                    The following course/s require you to make a declaration
                    that the above delegate{delegateCount > 1 ? 's' : ''}{' '}
                    {delegateCount > 1 ? 'have' : 'has'} recently completed a
                    5-year background check to comply with Civil Aviation
                    Authority standards.
                  </p>
                  <div className={classes.courseList}>
                    {backgroundCheckCourses.map((course: Course) => (
                      <div className={classes.courseListItem} key={course.id}>
                        {course.name}
                      </div>
                    ))}
                  </div>
                  <div className={classes.checkboxRow}>
                    <CheckboxSingle
                      label={
                        <>
                          <span>
                            {`I can confirm that the above delegate${
                              delegateCount > 1 ? 's' : ''
                            } ${
                              delegateCount > 1 ? 'have' : 'has'
                            } successfully completed a background check and ${
                              delegateCount > 1 ? 'have' : 'has'
                            } a job with aviation security responsibility.`}{' '}
                          </span>
                          <br />
                          <br />
                          <span>
                            I further confirm that the online training must take
                            place in a controlled environment (such as a
                            classroom) without distractions, properly lit,
                            heated and ventilated. Delegates must have exclusive
                            access to a computer or laptop for the duration of
                            the online training. Delegates are able to take as
                            many breaks as needed. Delegates must complete this
                            course on their own, with nobody helping them, and
                            the exam at the end is to be closed book and
                            delegates are not allowed to consult any notes they
                            may have taken during the course.
                          </span>
                        </>
                      }
                      onChange={(checked) =>
                        setState((s) => ({
                          ...s,
                          backgroundCheckAccepted: checked
                        }))
                      }
                    />
                  </div>
                </>
              )}
              <div className={classes.checkboxRow}>
                <CheckboxSingle
                  label={'I agree to the TTC Terms of Business'}
                  onChange={(checked) =>
                    setState((s) => ({ ...s, termsAccepted: checked }))
                  }
                />
              </div>
            </>
          )}
          {step === 'payment' && state.success && (
            <div className={classes.successPanel}>
              <FontAwesomeIcon
                icon={faCircleCheck}
                className={classes.successIcon}
              />
              <div className={classes.successTitle}>
                Thanks, your order was successful
              </div>
              <p className={classes.successText}>
                {delegateCount} delegate{delegateCount > 1 ? 's have' : ' has'}{' '}
                been booked onto {state.courses?.[0]?.name ?? 'the course'}.
                Your receipt and invoice have been emailed to you — feel free
                to close this window.
              </p>
            </div>
          )}
          {step === 'payment' && !state.success && (
            <>
              <p className={classes.helpText}>
                You have opted to book {delegateCount} Delegate
                {delegateCount > 1 ? 's' : ''} on the following Course
              </p>
              <Payment
                courses={
                  state.courses?.length > 0
                    ? state.courses.map((course: any) => ({
                        ...course,
                        id: course.ident || course.id
                      }))
                    : []
                }
                userUUIDs={
                  state.users
                    ?.filter(Boolean)
                    .map((user: { uuid: string }) => user.uuid) ?? []
                }
                isContract={isContract}
                onSuccess={() => setState((s) => ({ ...s, success: true }))}
                onError={(error: GraphError) =>
                  setState((s) => ({ ...s, error }))
                }
                hideOrderCard
                registerSubmit={(submit) => (placeOrder.current = submit)}
              />
              {state.error !== undefined && (
                <div className={classes.errorBanner}>
                  Transaction failed: {state.error?.message}
                  {(state.error?.extensions?.type === 'ErrCourseFull' ||
                    state.error?.extensions?.type ===
                      'ErrTooManyParticipants') && (
                    <Button
                      archetype="flat"
                      onClick={() => {
                        setStep('courses');
                        setState((s) => ({ ...s, error: undefined }));
                      }}
                    >
                      Back to Courses
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <div className={classes.footer}>
          {state.success ? (
            <div className={classes.footerButtons}>
              <Button archetype="flatPrimary" onClick={onClose}>
                Done
              </Button>
            </div>
          ) : (
            <>
              {footerTotal}
              <div className={classes.footerButtons}>
                {step === 'courses' ? (
                  <>
                    <Button archetype="flat" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button
                      archetype="flatPrimary"
                      disabled={
                        !(
                          state.courses?.length > 0 &&
                          state.users?.length > 0 &&
                          state.users[0]
                        )
                      }
                      onClick={() => setStep('terms')}
                    >
                      Continue to Terms
                    </Button>
                  </>
                ) : step === 'terms' ? (
                  <>
                    <Button archetype="flat" onClick={() => setStep('courses')}>
                      Back
                    </Button>
                    <Button
                      archetype="flatPrimary"
                      disabled={
                        !(
                          state.termsAccepted &&
                          (!requiresBackgroundChecks ||
                            state.backgroundCheckAccepted)
                        )
                      }
                      onClick={() => setStep('payment')}
                    >
                      Continue to Payment
                    </Button>
                  </>
                ) : (
                  <>
                    <Button archetype="flat" onClick={() => setStep('terms')}>
                      Back
                    </Button>
                    <Button
                      archetype="flatPrimary"
                      onClick={() => placeOrder.current?.()}
                    >
                      {!isContract && total > 0
                        ? 'Pay now'
                        : 'Place your order'}
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingModal;
